import React from 'react';
import { useHistory } from 'react-router-dom';
import { Formik, FormikActions } from 'formik';
import { Stack, Typography, Box } from '@mui/material';

import TextField from '@ui-lib/components/Inputs/TextField/FormikTextField';
import Button from '@ui-lib/components/Button';
import { createSx } from '@ui-lib/utils';

import { getCurrentLocation } from '../../services/geolocation';
import { loginUser, checkUser } from '../../api/auth';
import { useAuthContext } from '../SplashScreen/AuthContext';

import { getUserCompanies } from '../../redux/actions/userCompanies';
import logoIcon from '../../assets/logo-with-text.svg';
import bgImage from '../../assets/login-bg.jpg';
import businessIcon from '../../assets/business-icon.svg';
import { useAppDispatch } from '../../redux/types';
import { initialValues, LoginPinForm, validationSchema } from './const';

const sx = createSx({
  root: {
    width: '100%',
    height: '100%',
    backgroundColor: 'background.secondary',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    p: 2,
    boxSizing: 'border-box',
    margin: 'auto',
    justifyContent: 'center',
    backgroundImage: `url(${bgImage})`,
    backgroundSize: 'cover',
    backgroundRepeat: 'no-repeat',
  },
  logo: {
    height: 28,
    marginBottom: 4
  },
  form: {
    width: '100%',
    maxWidth: 400,
    padding: 2,
    borderRadius: 2,
    backgroundColor: 'background.paper',
    boxSizing: 'border-box',
  },

  input: {
    width: '100%',
  },
});

export const LoginPinPage = () => {
  const history = useHistory();
  const { setUser } = useAuthContext();
  const dispatch = useAppDispatch();

  const onSubmit = async (values: LoginPinForm, { setSubmitting, setErrors }: FormikActions<LoginPinForm>) => {
    try {
      const position = await getCurrentLocation().catch(() => null);
      if (!position) {
        throw new Error('Unable to get current location');
      }

      await loginUser({
        pin: parseInt(values.pin, 10),
        lat: position.coords.latitude,
        lng: position.coords.longitude,
      });
      const user = await checkUser(); // Assuming this function checks the user and sets the token
      await dispatch(getUserCompanies());
      setUser(user);
      const historyState = history.location.state as { from: string } | undefined;
      history.push(historyState?.from ?? '/create');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Login failed';
      setErrors({ pin: errorMessage });
      setSubmitting(false);
    }
  };
  return (
    <Box sx={sx.root}>
      <Box component="img" src={logoIcon} sx={sx.logo} />
      <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={onSubmit}>
        {({ handleSubmit, isSubmitting }) => (
          <Stack sx={sx.form} spacing={2}>
            <Box display="flex" justifyContent="center" alignItems="center">
              <img src={businessIcon} alt="Business Icon" style={{ width: 64, height: 64 }} />
            </Box>
            <Typography variant="h4" align="center">
              Please enter your PIN
            </Typography>
            <TextField name='pin' label='PIN' sx={sx.input} />
            <Button
              color="brand"
              testID='submit-button'
              onClick={() => { handleSubmit(); }}
              disabled={isSubmitting}
              loading={isSubmitting}
            >
              Submit
            </Button>
          </Stack>
        )}
      </Formik>
    </Box>
  );
};
